import { Icon, ICONS } from '@bob/library'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { ProductInfoStockLabel } from '@websrc/App/Components/Details/ProductInfo/StockLabel/StockLabel'
import { formatPrice } from '@websrc/App/Helpers/formatting'
import { trackAddToCart } from '@websrc/App/Helpers/tracking'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { useSettings } from '@websrc/App/Hooks/useSettings'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { useBasketApi } from '@websrc/Basket/useBasketApi'
import { useBasketStateOptional } from '@websrc/Basket/useBasketState'
import { useProductMeta } from '@websrc/Basket/useProductMeta'
import React, { useMemo, useState } from 'react'

import { BUTTON_WIDTH } from '../Button/Button'
import { WhereToBuyButton } from '../Details/ProductInfo/Buttons/WhereToBuyButton/WhereToBuyButton'
import Heading from '../Heading/Heading'
import * as SC from './ProductCard.style'

export interface ProductCardProps {
  lazyLoadImage?: boolean
  product: {
    url: string
    price_discount: string
    price_list: string
    title: string
    thumbnail: any
    product_number: string
    school_grades: string
    is_retired: boolean
  }
  showCta?: boolean
  borderColor?: string
  onAddToCart?: (buttonText: string) => void
  onClick?: () => void
}

export const ProductCard = ({
  product,
  lazyLoadImage,
  showCta = true,
  borderColor,
  onAddToCart,
  onClick,
}: ProductCardProps) => {
  const locale = useLocale()
  const t = useStaticText()
  const settings = useSettings()
  const [hasError, setHasError] = useState(false)
  const basketApi = useBasketApi()
  const basket = useBasketStateOptional()

  const { url, price_discount, price_list, title, thumbnail, product_number, school_grades } =
    product

  const productMeta = useProductMeta(product_number)
  const productPath = `/${locale}/products`

  const AddToCart = () =>
    useMemo(
      () => (
        <>
          <SC.AddToCartButton
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()

              void basketApi(`/items/add/${product_number}/1`, 'POST').then(([err]) => {
                if (err) {
                  setHasError(true)
                } else if (basket) {
                  trackAddToCart('product card add to cart', productMeta, 1, price_discount, basket)
                  onAddToCart?.(t.ecom.add_to_cart)
                }
              })
            }}
          >
            <Icon icon={ICONS.ARROW_RIGHT} size={16} />
            <span>{t.ecom.add_to_cart}</span>
          </SC.AddToCartButton>
          {hasError && <SC.ErrorMessage>{t.ecom.error_message_add_to_cart}</SC.ErrorMessage>}
        </>
      ),
      []
    )

  return (
    <SC.Item
      aria-label={title}
      onClick={onClick}
      to={`${productPath}${url?.replace(productPath, '')}`}
    >
      <SC.ThumbnailWrapper borderColor={borderColor} data-testid={'thumbnail-wrapper'}>
        <SC.TopWrapper>
          {school_grades && (
            <SC.GradesWrapper>
              <Icon icon={ICONS.GRADUATE} size={16} />
              {school_grades}
            </SC.GradesWrapper>
          )}
          <SC.LabelWrapper>
            <ProductInfoStockLabel
              productDetails={{
                valid_from_date: productMeta.embargo_date,
                product_status: productMeta.product_status,
                is_retired: productMeta.is_retired,
              }}
              show={true}
            />
          </SC.LabelWrapper>
        </SC.TopWrapper>
        <Asset
          className={'productBandImage'}
          {...thumbnail}
          altText={thumbnail.altText}
          fit={'bounds'}
          height={260}
          lazyLoading={lazyLoadImage}
          sizes={'(min-width: 1024px) 390px, (min-width: 768px) 260px, 200px'}
          srcSetWidths={[300, 390, 600, 780]}
          video={undefined}
          width={260}
        />
      </SC.ThumbnailWrapper>
      {locale === 'en-us' && !productMeta.is_retired && (
        <SC.PriceWrapper>
          <SC.OriginalPrice isDiscounted={price_discount !== price_list}>
            {formatPrice(price_list)}
          </SC.OriginalPrice>
          {price_discount !== price_list && (
            <SC.DiscountPrice>{formatPrice(price_discount)}</SC.DiscountPrice>
          )}
        </SC.PriceWrapper>
      )}
      <Heading headline={title} level={3} size={'s'} />
      {locale === 'en-us' &&
        showCta &&
        !productMeta.channelsight_asset_id &&
        (productMeta.is_retired ? (
          <SC.Retired onClick={(e) => e.stopPropagation()} to={`/${locale}${settings.contactUrl}`}>
            <Icon icon={ICONS.ARROW_RIGHT} size={16} />
            <span>{t.ecom.retired}</span>
          </SC.Retired>
        ) : (
          <AddToCart />
        ))}
      {showCta && productMeta.channelsight_asset_id && product_number && (
        <WhereToBuyButton
          assetId={productMeta.channelsight_asset_id}
          color={'blue'}
          colorMode={'light'}
          iconName={'ARROW_RIGHT'}
          iconPosition={'left'}
          productNumber={
            productMeta.channelsight_virtual_sku
              ? productMeta.channelsight_virtual_sku
              : product_number
          }
          removePadding={true}
          size={'medium'}
          type={'tertiary'}
          width={BUTTON_WIDTH.Auto}
        />
      )}
    </SC.Item>
  )
}
